import { useTranslate } from "@refinedev/core";
import { useNavigate } from "react-router-dom";
import { useAntTheme } from "hooks/useAntTheme";
import { FileVideoIcon, LightningIcon } from "@phosphor-icons/react";
import styled from "styled-components";
import { useProjectWizardStore } from "../../projects/useProjectWizardStore";
import { useAnalytics } from "hooks/useAnalytics";
import { AnalyticsEvents } from "types/analytics";
import { useSoftPaywall } from "@/providers/SoftPaywallProvider";
import { useCreateExperienceEntry } from "hooks/useCreateExperienceEntry";
import noiseTexture from "../../../../assets/noise-texture.png";
import noiseTextureUpgrade from "../../../../assets/noise-texture-upgrade-button.png";
import { radius } from "@libertify/ui";

export type CreateExperienceCardProps = {
  isProjectQuotaReached: boolean;
  isNearQuota?: boolean;
};

export const CreateExperienceCard = ({
  isProjectQuotaReached,
  isNearQuota = false,
}: CreateExperienceCardProps) => {
  const t = useTranslate();
  const navigate = useNavigate();
  const { theme } = useAntTheme();
  const { setWizardStartTime } = useProjectWizardStore();
  const { trackEvent } = useAnalytics();
  const { paywallFlow } = useSoftPaywall();
  const { startCreateExperience, chooserModal } = useCreateExperienceEntry();

  const showUpgradeVariant = isNearQuota || isProjectQuotaReached;

  const handleCreateExperience = () => {
    setWizardStartTime(Date.now());
    trackEvent(AnalyticsEvents.ProjectCreateInit, {
      page: "home",
      paywall_flow: paywallFlow,
    });
    startCreateExperience();
  };

  const handleUpgrade = (_e: React.MouseEvent<HTMLButtonElement>) => {
    trackEvent(AnalyticsEvents.UpgradeButtonClick, {
      location: "home_card",
    });
    navigate("/subscription");
  };

  if (showUpgradeVariant) {
    return (
      <UpgradeCard onClick={handleUpgrade}>
        <UpgradeTextColumn>
          <UpgradeTitle>
            {t("pages.home.index.keepImprovingTitle")}
          </UpgradeTitle>
          <UpgradeDesc>
            {t("pages.home.index.keepImprovingDesc")}{" "}
            <span
              dangerouslySetInnerHTML={{
                __html: t("pages.home.index.keepImprovingProBuiltForYou"),
              }}
            />
          </UpgradeDesc>
        </UpgradeTextColumn>
        <UpgradeButton type="button" onClick={handleUpgrade}>
          <LightningIcon size={24} />
          {t("pages.home.index.upgradeNow")}
        </UpgradeButton>
      </UpgradeCard>
    );
  }

  return (
    <>
      <Card
        onClick={isProjectQuotaReached ? undefined : handleCreateExperience}
        $primaryColor={theme.colorPrimary}
        $disabled={isProjectQuotaReached}
      >
        <BackgroundIcon aria-hidden="true">
          <FileVideoIcon size={200} />
        </BackgroundIcon>
        <IconWrapper $primaryColor={theme.colorPrimary}>
          <FileVideoIcon size={25} />
        </IconWrapper>
        <Title>{t("pages.home.index.createExperienceToday")}</Title>
        <Subtitle>{t("pages.home.index.createExperienceTodayDesc")}</Subtitle>
      </Card>
      {chooserModal}
    </>
  );
};

const Card = styled.button<{
  $primaryColor: string;
  $disabled?: boolean;
}>`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;
  padding: 32px;
  border-radius: ${radius.box}px;
  border: 3px solid #099e94;
  background: transparent;
  width: 300px;
  flex-shrink: 0;
  height: 228px;
  overflow: hidden;

  @media (width <= 768px) {
    width: 255px;
    height: 194px;
    padding: 24px;
  }

  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  text-align: left;
  font-family: "Inter", sans-serif;
  transition: box-shadow 0.2s;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(54deg, #67dfd7 39.96%, #2faaa2 97.36%);
    z-index: 0;
    pointer-events: none;
  }

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background-image: url(${noiseTexture});
    background-repeat: repeat;
    opacity: 0.6;
    z-index: 1;
    pointer-events: none;
  }

  > * {
    position: relative;
    z-index: 2;
  }

  &:hover {
    box-shadow: ${({ $disabled }) =>
      $disabled ? "none" : "0 2px 12px rgba(0, 0, 0, 0.06)"};
  }
`;

const BackgroundIcon = styled.div`
  position: absolute;
  top: 50%;
  right: -10px;
  transform: translateY(-50%);
  color: #0c2a28;
  opacity: 0.08;
  pointer-events: none;
  line-height: 0;

  @media (width <= 768px) {
    right: -10px;

    svg {
      width: 160px;
      height: 160px;
    }
  }
`;

const IconWrapper = styled.div<{ $primaryColor: string }>`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  color: #0c2a28;
  margin-bottom: 4px;
`;

const Title = styled.div`
  font-family: "Inter", sans-serif;
  font-size: 28px;
  font-weight: 600;
  line-height: 100%;
  letter-spacing: -1.12px;
  color: #0c2a28;

  @media (width <= 768px) {
    font-size: 20px;

    svg {
      width: 160px;
      height: 160px;
    }
  }
`;

const Subtitle = styled.div`
  font-family: "Inter", sans-serif;
  font-size: 15px;
  font-weight: 400;
  line-height: 135%;
  letter-spacing: -0.3px;
  color: #0c2a28;
`;

const UpgradeCard = styled.button`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: stretch;
  justify-content: space-between;
  gap: 24px;
  padding: 24px;
  border-radius: ${radius.box}px;
  border: 3px solid #fff;
  background: transparent;
  width: 626px;
  flex-shrink: 0;
  height: 228px;
  overflow: hidden;
  cursor: pointer;
  text-align: left;
  font-family: "Inter", sans-serif;
  transition:
    opacity 0.2s,
    box-shadow 0.2s;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(92deg, #6946d5 22.22%, #5423ec 80.77%);
    z-index: 0;
    pointer-events: none;
  }

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background-image: url(${noiseTextureUpgrade});
    background-repeat: repeat;
    opacity: 0.6;
    z-index: 1;
    pointer-events: none;
  }

  > * {
    position: relative;
    z-index: 2;
  }

  &:hover {
    opacity: 0.92;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  }

  @media (width <= 768px) {
    flex-direction: column;
    align-items: stretch;
    justify-content: space-between;
    gap: 12px;
    width: 100%;
    max-width: 345px;
    height: auto;
    min-height: 194px;
    padding: 20px;
  }
`;

const UpgradeTextColumn = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;
  flex: 0 0 60%;
  max-width: 60%;
  min-width: 0;

  @media (width <= 768px) {
    flex: 1 1 auto;
    max-width: 100%;
    justify-content: flex-start;
    gap: 6px;
  }
`;

const UpgradeTitle = styled.div`
  color: #fff;
  font-family: Poppins, sans-serif;
  font-size: 28px;
  font-style: normal;
  font-weight: 600;
  line-height: 118%;
  letter-spacing: -1.12px;

  @media (width <= 768px) {
    font-size: 20px;
    letter-spacing: -0.8px;
  }
`;

const UpgradeDesc = styled.div`
  color: #fff;
  font-family: "Inter", sans-serif;
  font-size: 15px;
  font-style: normal;
  font-weight: 300;
  line-height: 135%; /* 20.25px */
  letter-spacing: -0.3px;

  strong {
    color: #ffffff;
    font-weight: 700;
  }

  @media (width <= 768px) {
    font-size: 13px;
    line-height: 130%;
  }
`;

const UpgradeButton = styled.button`
  font-family: "Inter", sans-serif;
  font-size: 15px;
  font-style: normal;
  font-weight: 500;
  line-height: 16px;
  width: 171px;
  height: 40px;
  letter-spacing: -0.15px;
  flex-shrink: 0;
  align-self: flex-end;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 20px;
  border-radius: ${radius.pill}px;
  border: 2px solid #3bd4ca;
  background: #ffffff;
  color: #6743d7;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }

  @media (width <= 768px) {
    width: auto;
    max-width: 100%;
    height: 36px;
    padding: 8px 16px;
    font-size: 14px;
  }
`;
